import { useState } from 'react';
import type {
  StakeEventGloss,
  StakeModeRoundStats,
  StakeModeStats,
  StakeVolatilityClass,
} from '@sr-test-wizard/shared';
import { MarkdownView } from '../extras-game-kb/ReportView';

type Tab = 'OVERVIEW' | 'DISTRIBUTION' | 'STATISTICS' | 'ROUNDS';

/**
 * Props that thread the on-demand AI round analysis (event glossary + per-mode
 * narrative) and its generate action down to the ROUNDS tab.
 */
export interface ExplainProps {
  glossary?: StakeEventGloss[];
  narrative?: string;
  onExplain?: (mode: string) => void;
  explainBusy?: boolean;
  explainError?: string;
}

/** Top of the meter scale (std in stake units); knob clamps to this. */
const VOL_SCALE = 30;

function volClass(v: StakeVolatilityClass | null): string {
  if (v === 'HIGH') return 'bad';
  if (v === 'MEDIUM') return 'warn';
  if (v === 'LOW') return 'good';
  return 'muted';
}

function VolatilityMeter({ r }: { r: StakeModeStats }) {
  const pct = Math.min(100, Math.max(0, (r.std / VOL_SCALE) * 100));
  return (
    <div className="vol-meter">
      <div className="vol-meter-head">
        <span className="vol-meter-label">VOLATILITY</span>
        <span className="vol-meter-value">{r.std.toFixed(2)}</span>
        <span className={`chip ${volClass(r.volatilityClass)}`}>{r.volatilityClass ?? 'N/A'}</span>
      </div>
      <div className="vol-meter-track">
        <div className="vol-meter-knob" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
}

function oneIn(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return '—';
  return `1 in ${Math.round(n).toLocaleString()}`;
}

function spins(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return '—';
  return `${Math.ceil(n).toLocaleString()} spins`;
}

function pct(n: number, digits = 2): string {
  return `${n.toFixed(digits)}%`;
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="metric-tile">
      <span className="mt-label">{label}</span>
      <span className="mt-value">{value}</span>
      {hint ? <span className="mt-hint muted">{hint}</span> : null}
    </div>
  );
}

function Overview({ r }: { r: StakeModeStats }) {
  const dead = r.probZero * 100;
  const sub = r.subBetRate * 100;
  const win = r.winRate * 100;
  return (
    <div className="mode-tab-body">
      <h4 className="section-label">Detailed metrics</h4>
      <VolatilityMeter r={r} />
      <div className="metric-tile-grid">
        <Tile label="RTP" value={pct(r.rtpPct)} />
        <Tile label="Mean payout" value={`${r.mean.toFixed(4)}x`} />
        <Tile label="Hit rate" value={oneIn(r.hitRate)} hint={pct(r.hitRatePct)} />
        <Tile label="Max multiplier" value={`${r.maxMultiplier.toLocaleString()}x`} />
        <Tile label="Std dev" value={`${r.std.toFixed(3)}x`} />
        <Tile label="Unique payouts" value={r.uniquePayouts.toLocaleString()} />
        <Tile label="Most probable" value={oneIn(r.mostProbableHitRate)} />
        <Tile label="Max-win odds" value={oneIn(r.maxWinHitRate)} />
      </div>

      <h4 className="section-label">Outcome breakdown</h4>
      <div className="outcome-bar" role="img" aria-label="outcome breakdown">
        {dead > 0 ? (
          <span className="ob-seg ob-dead" style={{ width: `${dead}%` }} title={`Dead ${pct(dead, 1)}`} />
        ) : null}
        {sub > 0 ? (
          <span className="ob-seg ob-sub" style={{ width: `${sub}%` }} title={`Sub-bet ${pct(sub, 1)}`} />
        ) : null}
        {win > 0 ? (
          <span className="ob-seg ob-win" style={{ width: `${win}%` }} title={`Win ${pct(win, 1)}`} />
        ) : null}
      </div>
      <div className="outcome-legend small">
        <span><i className="ob-dot ob-dead" /> Dead {pct(dead, 1)}</span>
        <span><i className="ob-dot ob-sub" /> Sub-bet {pct(sub, 1)}</span>
        <span><i className="ob-dot ob-win" /> Win {pct(win, 1)}</span>
      </div>

      <h4 className="section-label">Streaks &amp; cadence</h4>
      <div className="metric-tile-grid">
        <Tile label="Avg between wins" value={spins(r.hitRate)} />
        <Tile label="Avg between profit" value={spins(r.avgSpinsBetweenProfit)} />
        <Tile label="Worst zero streak" value={r.worstZeroStreak === null ? '—' : `${r.worstZeroStreak} spins`} hint="1-in-1000" />
        <Tile label="Worst loss streak" value={r.worstLossStreak === null ? '—' : `${r.worstLossStreak} spins`} hint="1-in-1000" />
      </div>
    </div>
  );
}

function Distribution({ r }: { r: StakeModeStats }) {
  if (!r.distribution.length) {
    return <p className="muted small mode-tab-body">No distribution data (re-run verification to populate).</p>;
  }
  const maxContrib = Math.max(...r.distribution.map((b) => Math.abs(b.rtpContributionPct)), 0.0001);
  return (
    <div className="mode-tab-body" style={{ overflowX: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>multiplier range</th>
            <th>distinct payouts</th>
            <th>effective hit rate</th>
            <th>RTP contribution</th>
          </tr>
        </thead>
        <tbody>
          {r.distribution.map((b) => (
            <tr key={b.label} className={b.distinctCount === 0 ? 'muted' : undefined}>
              <td className="mono">{b.label}</td>
              <td>{b.distinctCount.toLocaleString()}</td>
              <td>{oneIn(b.effectiveHitRate)}</td>
              <td>
                <div className="dist-bar-wrap">
                  <div
                    className="dist-bar"
                    style={{ width: `${(Math.abs(b.rtpContributionPct) / maxContrib) * 100}%` }}
                  />
                  <span className="dist-bar-val mono small">{b.rtpContributionPct.toFixed(3)}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Statistics({ r }: { r: StakeModeStats }) {
  const recon =
    r.reconciles === null ? 'no declared RTP' : r.reconciles ? 'within 0.5pp' : 'off target';
  const stats: Array<[string, string]> = [
    ['Mode', r.mode],
    ['Bet cost', String(r.cost)],
    ['Outcomes (rows)', r.rows.toLocaleString()],
    ['Total weight', r.totalWeight],
    ['Unique payouts', r.uniquePayouts.toLocaleString()],
    ['RTP %', r.rtpPct.toFixed(4)],
    ['Reconciles', recon],
    ['Mean payout (x)', r.mean.toFixed(4)],
    ['Std dev (x)', r.std.toFixed(4)],
    ['Volatility', r.volatilityClass ?? 'N/A'],
    ['Min multiplier (x)', r.minMultiplier.toLocaleString()],
    ['Max multiplier (x)', r.maxMultiplier.toLocaleString()],
    ['Hit rate %', r.hitRatePct.toFixed(4)],
    ['Hit rate (1 in N)', oneIn(r.hitRate)],
    ['Win rate %', (r.winRate * 100).toFixed(4)],
    ['Sub-bet rate %', (r.subBetRate * 100).toFixed(4)],
    ['Dead (zero) %', (r.probZero * 100).toFixed(4)],
    ['P(payout < bet) %', (r.probLtBet * 100).toFixed(4)],
    ['Most probable (1 in N)', oneIn(r.mostProbableHitRate)],
    ['Max-win odds (1 in N)', oneIn(r.maxWinHitRate)],
    ['Avg spins between win', spins(r.hitRate)],
    ['Avg spins between profit', spins(r.avgSpinsBetweenProfit)],
    ['Worst zero streak (1/1000)', r.worstZeroStreak === null ? '—' : `${r.worstZeroStreak}`],
    ['Worst loss streak (1/1000)', r.worstLossStreak === null ? '—' : `${r.worstLossStreak}`],
  ];
  return (
    <div className="mode-tab-body">
      <div className="stat-grid">
        {stats.map(([k, v]) => (
          <div className="stat-item" key={k}>
            <span className="stat-k">{k}</span>
            <span className="stat-v mono">{v}</span>
          </div>
        ))}
      </div>
      {r.notes.length ? (
        <p className="small muted" style={{ marginTop: 8 }}>notes: {r.notes.slice(0, 5).join('; ')}</p>
      ) : null}
    </div>
  );
}

/**
 * On-demand AI analysis block at the bottom of the ROUNDS tab: a button that
 * generates (and caches) the event glossary + a gameplay narrative for this
 * mode, grounded in the analysed game-help screenshots. Separate from the chat.
 */
function AiAnalysis({ mode, explain }: { mode: string; explain?: ExplainProps }) {
  if (!explain || !explain.onExplain) return null;
  const { narrative, onExplain, explainBusy, explainError } = explain;
  const has = Boolean(narrative);
  return (
    <div className="ai-analysis">
      <div className="ai-analysis-head">
        <h4 className="section-label" style={{ margin: 0 }}>AI analysis</h4>
        <span className="spacer" />
        <button
          type="button"
          className="btn small"
          disabled={explainBusy}
          onClick={() => onExplain(mode)}
        >
          {explainBusy ? 'Analysing…' : has ? 'Regenerate' : 'Explain with AI'}
        </button>
      </div>
      {explainError ? (
        <p className="small" style={{ color: 'var(--bad)', marginTop: 8 }}>{explainError}</p>
      ) : null}
      {has ? (
        <div className="ai-analysis-body">
          <MarkdownView>{narrative as string}</MarkdownView>
        </div>
      ) : explainBusy ? (
        <p className="muted small" style={{ marginTop: 8 }}>
          Translating event types and writing the gameplay story from the game help…
        </p>
      ) : (
        <p className="muted small" style={{ marginTop: 8 }}>
          Generate a plain-language translation of the event types above plus a story-style
          walkthrough of a round in this mode, grounded in the analysed game-help screenshots.
        </p>
      )}
    </div>
  );
}

function Rounds({ rounds, explain }: { rounds: StakeModeRoundStats; explain?: ExplainProps }) {
  if (rounds.error) {
    return <p className="small mode-tab-body" style={{ color: 'var(--bad)' }}>{rounds.error}</p>;
  }
  const maxRtp = Math.max(...rounds.byCriteria.map((c) => Math.abs(c.rtpContributionPct)), 0.0001);
  const maxEvt = Math.max(...rounds.eventTypes.map((e) => e.count), 1);
  const noSemantics =
    rounds.byCriteria.every((c) => c.label === 'unspecified') &&
    rounds.eventTypes.every((e) => e.type === 'simulation');
  const glossByType = new Map((explain?.glossary ?? []).map((g) => [g.type, g]));
  const eventList = rounds.eventTypes.length ? (
    <div className="evt-list">
      {rounds.eventTypes.map((e) => {
        const g = glossByType.get(e.type);
        return (
          <div className="evt-row" key={e.type}>
            <span className="evt-name mono" title={g?.meaning || undefined}>
              {e.type}
              {g ? <span className="evt-friendly">{g.name}</span> : null}
            </span>
            <div className="evt-bar-wrap">
              <div className="evt-bar" style={{ width: `${(e.count / maxEvt) * 100}%` }} />
            </div>
            <span className="evt-count mono small">{e.count.toLocaleString()}</span>
          </div>
        );
      })}
    </div>
  ) : (
    <p className="muted small">No events recorded.</p>
  );

  const notes = rounds.notes.length ? (
    <p className="small muted" style={{ marginTop: 8 }}>notes: {rounds.notes.join('; ')}</p>
  ) : null;

  // Crash/instant games carry no `criteria` or win events - the criteria table
  // and feature/win mechanics would just be a row of misleading zeros, so we
  // hide them and show only what is real (the note, record count, event types).
  if (noSemantics) {
    return (
      <div className="mode-tab-body">
        <p className="small muted" style={{ marginBottom: 12 }}>
          This game encodes each round as a single opaque <span className="mono">simulation</span> event
          with no <span className="mono">criteria</span> or per-event semantics — typical of
          crash/instant-style games. Per-round-type classification isn&apos;t available; the gameplay
          shape lives in the multiplier distribution (see the DISTRIBUTION tab). Slots like Boom instead
          expose <span className="mono">basegame</span>/<span className="mono">freegame</span>/
          <span className="mono">wincap</span> and win events here.
        </p>
        <h4 className="section-label">Book</h4>
        <div className="metric-tile-grid">
          <Tile label="Book records" value={rounds.bookRecords.toLocaleString()} />
          <Tile label="Event types" value={rounds.eventTypes.length.toLocaleString()} />
        </div>
        <h4 className="section-label">Event types (occurrences across books)</h4>
        {eventList}
        {notes}
        <AiAnalysis mode={rounds.mode} explain={explain} />
      </div>
    );
  }

  return (
    <div className="mode-tab-body">
      <h4 className="section-label">Round outcomes (by criteria, weighted)</h4>
      {rounds.byCriteria.length ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>criteria</th>
                <th>rounds %</th>
                <th>RTP contribution</th>
                <th>distinct sims</th>
              </tr>
            </thead>
            <tbody>
              {rounds.byCriteria.map((c) => (
                <tr key={c.label}>
                  <td className="mono">{c.label}</td>
                  <td>{c.roundsPct.toFixed(3)}%</td>
                  <td>
                    <div className="dist-bar-wrap">
                      <div
                        className="dist-bar"
                        style={{ width: `${(Math.abs(c.rtpContributionPct) / maxRtp) * 100}%` }}
                      />
                      <span className="dist-bar-val mono small">{c.rtpContributionPct.toFixed(3)}%</span>
                    </div>
                  </td>
                  <td>{c.rounds.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted small">No criteria found in book records.</p>
      )}

      <h4 className="section-label">Mechanics</h4>
      <div className="metric-tile-grid">
        <Tile label="Feature rounds" value={`${rounds.featureRoundsPct.toFixed(3)}%`} hint="free-spins / bonus" />
        <Tile label="Wincap rounds" value={`${rounds.wincapRoundsPct.toFixed(4)}%`} />
        <Tile label="Distinct win symbols" value={rounds.distinctWinSymbols.toLocaleString()} />
        <Tile label="Max wins / round" value={rounds.maxWinsInRound.toLocaleString()} />
        <Tile label="Book records" value={rounds.bookRecords.toLocaleString()} />
      </div>

      <h4 className="section-label">Event types (occurrences across books)</h4>
      {eventList}
      {notes}
      <AiAnalysis mode={rounds.mode} explain={explain} />
    </div>
  );
}

/** Tabbed detail panel for a single selected mode. */
export function ModeDetail({
  row,
  rounds,
  explain,
  embedded = false,
}: {
  row: StakeModeStats;
  rounds?: StakeModeRoundStats;
  explain?: ExplainProps;
  embedded?: boolean;
}) {
  const [tab, setTab] = useState<Tab>('OVERVIEW');
  const tabs: Tab[] = ['OVERVIEW', 'DISTRIBUTION', 'STATISTICS', ...(rounds ? ['ROUNDS' as const] : [])];
  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      className={embedded ? 'mode-detail mode-detail-inline' : 'card mode-detail'}
      style={embedded ? undefined : { marginBottom: 16 }}
    >
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        {embedded ? null : <h3 style={{ margin: 0 }}>{row.mode}</h3>}
        <span className="spacer" />
        <div className="tab-bar">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              className={`tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      {tab === 'OVERVIEW' && <Overview r={row} />}
      {tab === 'DISTRIBUTION' && <Distribution r={row} />}
      {tab === 'STATISTICS' && <Statistics r={row} />}
      {tab === 'ROUNDS' && rounds && <Rounds rounds={rounds} explain={explain} />}
    </Wrapper>
  );
}
